import type { ComponentProps, ReactNode } from "react";

import {
  SessionPaneFloatingControls,
  SessionPaneTabStripView,
  SessionPaneView,
  type SessionPaneTabItem,
} from "@product/components/app/sessions/session-pane-view";
import {
  WorkflowAboutTrigger,
  WorkflowActionsTrigger,
  WorkflowPaneBodyView,
  WorkflowRunControlsView,
  WorkflowViewControls,
  workflowStatusText,
} from "@product/components/app/workflows/workflow-pane-view";
import { WorkflowResultsView } from "@product/components/app/workflows/workflow-results-view";
import {
  WorkflowOutputCellView,
  WorkflowOutputDetailsTrigger,
  WorkflowOutputTableView,
} from "@product/components/app/workflows/workflow-output-table-view";
import {
  RecordTableCellView,
  RecordTableGridView,
  RecordTableHeaderCellView,
  RecordTableHeaderRowView,
  RecordTableRowView,
} from "@product/app/workspace/channels/[channelId]/record-table-grid-view";
import { ROW_HEIGHT, fieldColumnSize } from "@product/app/workspace/channels/[channelId]/record-table-state";
import { TableColumnResizeHandle } from "@product/components/ui/table-column-resize-handle";

import type { AppThemeTone } from "@/components/editorial/product-bridge/app-surface";

import { SHOT_WINDOW_HEIGHT_CLASS, ShotAppFrame } from "./window";
import { WORKFLOW_RESULT_COLUMNS, WORKFLOW_RESULT_ROWS, type WorkflowRunFrame } from "./workflow-run";
import type { ShotWorkspace } from "./workspace";

const noop = () => {};

/**
 * The pane's tabs as a workflow page publishes them (use-session-pane-tabs):
 * the workflow's views (WORKFLOW_PANE_TABS), then the authoring session's
 * details under the name a workflow page gives them.
 */
export const WORKFLOW_PANE_TAB_ITEMS: readonly SessionPaneTabItem[] = [
  { id: "workflow:summary", kind: "workflow", title: "Summary" },
  { id: "workflow:canvas", kind: "workflow", title: "Canvas" },
  { id: "workflow:runs", kind: "workflow", title: "Runs" },
  { id: "workflow:results", kind: "workflow", title: "Results" },
  { id: "workflow:code", kind: "workflow", title: "Code" },
  { id: "details", kind: "details", title: "Session" },
];

/**
 * The output's grid at rest, as WorkflowOutputTable renders it through
 * RecordTableGrid's views: a column per inferred field at its default width,
 * each header a sort button (none sorted) with its resize handle, and every
 * row `index * ROW_HEIGHT` down the body. Rows that are not channel records
 * and have no row action get no pinned column.
 */
function ShotWorkflowOutputGrid() {
  const widths = WORKFLOW_RESULT_COLUMNS.map((column) => fieldColumnSize(column).size);
  const tableWidth = widths.reduce((sum, width) => sum + width, 0);
  return (
    <RecordTableGridView
      caption="Output"
      tableWidth={tableWidth}
      bodyHeight={WORKFLOW_RESULT_ROWS.length * ROW_HEIGHT}
      header={
        <RecordTableHeaderRowView>
          {WORKFLOW_RESULT_COLUMNS.map((column, index) => (
            <RecordTableHeaderCellView
              key={column.key}
              width={widths[index]!}
              label={column.label}
              name={column.label}
              sortable
              resizeHandle={<TableColumnResizeHandle aria-label={`Resize ${column.label}`} active={false} />}
            />
          ))}
        </RecordTableHeaderRowView>
      }
    >
      {WORKFLOW_RESULT_ROWS.map((row, index) => (
        <RecordTableRowView key={index} index={index} start={index * ROW_HEIGHT} tableWidth={tableWidth}>
          {WORKFLOW_RESULT_COLUMNS.map((column, columnIndex) => (
            <RecordTableCellView key={column.key} width={widths[columnIndex]!} role="gridcell" tabIndex={-1}>
              <WorkflowOutputCellView type={column.type} title={row[column.key]!}>
                {row[column.key]}
              </WorkflowOutputCellView>
            </RecordTableCellView>
          ))}
        </RecordTableRowView>
      ))}
    </RecordTableGridView>
  );
}

/**
 * The workflow's output as WorkflowStepOutput shows a step's one table: the
 * Results tab's step picker and status line leading its toolbar, Search,
 * Columns and Details, the tab's notices, then the grid.
 */
function ShotWorkflowOutput({ leading, notices }: { leading: ReactNode; notices: ReactNode }) {
  return (
    <WorkflowOutputTableView title="Output" leading={leading} trailing={<WorkflowOutputDetailsTrigger />} notices={notices}>
      <ShotWorkflowOutputGrid />
    </WorkflowOutputTableView>
  );
}

/** What the canvas floats at its top right: the workflow's controls, and the shown run's. */
export type ShotCanvasTopRight = { controls: ReactNode; runControls: ReactNode };

/**
 * A workflow open in the application frame, as its page shows it at the
 * pane's Full preset: the pane over the whole window below the menu bar,
 * its tab strip, the canvas (or the Results tab) with the run on it and the
 * workflow's and the run's controls at its top right, and the floating
 * toolbar with the workflow's status. The app tells how a run went in a
 * toast, which a shot leaves out: the status line and the steps tell it. Everything inside is the product's own views over the
 * frame's fixture; WorkflowPanel composes the same views around live queries.
 *
 * The canvas renders in the browser only, as the app's does: `canvas` draws
 * the client graph around what it floats at its top right, or is omitted (a
 * server-rendered frame, or before the graph loads), as the app shows while
 * its canvas chunk loads.
 */
export function ShotWorkflowWindow({
  tone = "light",
  workspace,
  frame,
  canvas,
  className,
  heightClass = SHOT_WINDOW_HEIGHT_CLASS,
  ...props
}: {
  tone?: AppThemeTone;
  workspace: ShotWorkspace;
  frame: WorkflowRunFrame;
  canvas?: (topRight: ShotCanvasTopRight) => ReactNode;
  className?: string;
  heightClass?: string;
} & Omit<ComponentProps<"div">, "children">) {
  const { workflow, sample, run, tab, starting, running, now } = frame;
  const title = workflow.title ?? workflow.definition.title;
  // WorkflowPanel: busy while a run of the session works or a sample is starting.
  const busy = starting || running;
  return (
    <ShotAppFrame tone={tone} workspace={workspace} className={className} heightClass={heightClass} {...props}>
      {/* The pane's split at its Full preset: the pane alone, across the window. */}
      <div className="relative flex min-h-0 min-w-0 flex-1">
        <SessionPaneView
          top={
            <SessionPaneTabStripView
              tabs={WORKFLOW_PANE_TAB_ITEMS}
              activeId={`workflow:${tab}`}
              className="min-w-0 flex-1 [&_[role=tablist]]:border-b-0"
            />
          }
          controls={
            <SessionPaneFloatingControls
              title={title}
              subtitle={workflowStatusText({
                workflow,
                sample: sample ?? undefined,
                running,
                editing: false,
                dirty: false,
                now,
              })}
              mode="full"
              closeLabel="Back to workflows"
              actions={
                <>
                  <WorkflowAboutTrigger />
                  <WorkflowActionsTrigger workflowTitle={title} disabled={busy} />
                </>
              }
            />
          }
        >
          <WorkflowPaneBodyView
            tab={tab}
            canvas={
              canvas?.({
                controls: <WorkflowViewControls workflow={workflow} busy={busy} starting={starting} />,
                runControls: run ? <WorkflowRunControlsView run={run} /> : null,
              }) ?? null
            }
          >
            {tab === "results" ? (
              <WorkflowResultsView
                workflow={workflow}
                running={running}
                latestRunId={sample?.runId}
                sample={sample ?? undefined}
                now={now}
                // The step picker is a controlled select, so it takes a handler; no step changes in a shot.
                onSelectStep={noop}
                output={({ leading, notices }) => <ShotWorkflowOutput leading={leading} notices={notices} />}
              />
            ) : null}
          </WorkflowPaneBodyView>
        </SessionPaneView>
      </div>
    </ShotAppFrame>
  );
}
